import { differenceInSeconds } from 'date-fns';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    addDurationToDate,
    autoinject,
    bind,
    componentReaction,
    displayFormatTime,
    displayUtcToZonedTimeFactory,
    Duration,
    inject,
    LiveChart,
    Loader,
    subDurationFromDate,
    TasksStore,
    Tooltip,
    Mutex,
    PopoutHandle,
    toArray,
    CommonAppStore,
    minMax,
    Enums,
} from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { AppStore } from '../../../Core/AppStore';
import { DisplayListViewModel } from '../../../DisplayFilters/DisplayListViewModel';
import { UnitsStore } from '../../../Units/UnitsStore';
import { ActiveDisplay } from '../../ActiveDisplay';
import { DisplaysChartsStore } from './DisplaysChartsStore';
import { DisplaysStore } from '../../DisplaysStore';
import { debounce } from 'lodash';
import { DisplayDetailsPopout } from '../../DisplayDetailsPopout';
import { BsArrowsAngleExpand } from '@react-icons/all-files/bs/BsArrowsAngleExpand';
import { BsExclamationCircleFill } from '@react-icons/all-files/bs/BsExclamationCircleFill';
import { BsExclamationTriangle } from '@react-icons/all-files/bs/BsExclamationTriangle';
import { BsExclamationTriangleFill } from '@react-icons/all-files/bs/BsExclamationTriangleFill';

export interface ILiveChartCardProps {
    readonly display: ActiveDisplay;
    readonly history: any;
    readonly startTime?: Date;
    readonly vm: DisplayListViewModel;
    readonly className?: string;
    readonly chartNumber?: number;
}

@autoinject
@observer
export class LiveChartCard extends React.Component<ILiveChartCardProps> {
    public constructor(props: ILiveChartCardProps) {
        super(props);
        const trackedValues = computed(() => [
            this.selectedChart,
            this.appStore.storage.liveChartSettings.liveChartTimeWindow,
            this.startTime,
            this.maxValue,
            this.appStore.storage.liveChartSettings.triggerSecsPerSampleUpdate,
        ]);
        componentReaction(this, () => trackedValues.get(), this.debouncedLoadTrackData);
        componentReaction(this, () => this.props.startTime, this.onStartTimeChange);
        componentReaction(this, () => this.dataOutOfBounds, this.onDataOutOfBoundsChange);
    }

    @action
    public async componentDidMount() {  
        await this.loadDataAsync(2);
    }


    public async componentWillUnmount() {
        if (this.refreshTimer) window.clearInterval(this.refreshTimer);
        if (this.abortController) this.abortController.abort();
        this.appStore.showHeader();
        this.commonAppStore.chartHasDataOutOfBounds = false;
    }

    public render() {
        const { display, className } = this.props;
        const loading = this.loading && !this.liveRefreshing;

        return (
            <div className={className}>
                {!loading &&
                    this.chartConfig &&
                    this.selectedChart &&
                    this.trackDatas &&
                    this.endTime &&
                    this.startTime &&
                    !this.error && (
                    <LiveChart
                        chartConfig={this.selectedChart}
                        displaySerialNumber={display.serialNumber}
                        trackDatas={this.trackDatas}
                        className="p-2"
                        mobile={this.appStore.isMobile}
                        hideLegend={this.appStore.storage.liveChartSettings.liveChartHideLegend}
                        startTime={this.startTime}
                        endTime={this.endTime}
                        firstAvailableSampleTime={this.chartConfig.firstAvailableSampleTime}
                        header={
                            <div className="chart-container">
                                <div className="d-flex align-items-center">
                                    {this.props.chartNumber &&
                                        <span className="chart-number px-2 py-1">{this.props.chartNumber}</span>
                                    }
                                    <Tooltip message={this.tooltipMessage}>
                                        <a onClick={this.openPopout} className="btn btn-link p-0">
                                            <>
                                                {display.serialNumber}{' '}
                                                {display.roleInUnit != 0 &&
                                                    <div className="d-inline-block p-0">
                                                        ({Enums.toString(Enums.RoleInUnit, display.roleInUnit)})
                                                    </div>
                                                }
                                            </>
                                        </a>
                                    </Tooltip>
                                        &nbsp;
                                    <div className="d-inline-block ms-auto">
                                        {this.dataOutOfBounds && (
                                            <div className="d-inline-block mx-1">
                                                {this.commonAppStore.liveChartStaticScaling && (
                                                    <button
                                                        className="btn btn-link p-0 ms-auto"
                                                        onClick={this.toggleLiveChartScaling}
                                                    >
                                                        <div className="badge rounded-pill bg-warning">
                                                                Data Out Of Bounds
                                                        </div>
                                                    </button>
                                                )}

                                                {!this.commonAppStore.liveChartStaticScaling && (
                                                    <button
                                                        className="btn btn-link p-0 ms-auto"
                                                        onClick={this.toggleLiveChartScaling}
                                                    >
                                                        <div className="badge rounded-pill bg-info">
                                                                Dynamically scaled
                                                        </div>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {this.startTimeMismatch && (
                                            <Tooltip message="Showing Earliest Data">
                                                <BsExclamationTriangleFill className="text-info f-12" />
                                            </Tooltip>
                                        )}
                                        <small className={this.statusClassName}>{display.statusMessageBrief}</small>{' '}
                                        {!this.displayLocation && (
                                            <small>
                                                {display.pollTime
                                                    ? displayFormatTime(
                                                        display.pollTime,
                                                        'h:mm:ss a ZZZZ',
                                                        display.serialNumber
                                                    )
                                                    : '-'}
                                            </small>
                                        )}
                                        {this.displayLocation && (
                                            <>
                                                <small>
                                                    {display.pollTime
                                                        ? displayFormatTime(
                                                            display.pollTime,
                                                            'h:mm:ss a',
                                                            display.serialNumber
                                                        )
                                                        : '-'}
                                                </small>
                                                <small className="ps-1">{this.displayLocation}</small>
                                            </>
                                        )}
                                        <LinkContainer to={`/displays/${display.serialNumber}/live-charts`}>
                                            <a className="btn btn-link py-0 f-18">
                                                <BsArrowsAngleExpand className="text-white" />
                                            </a>
                                        </LinkContainer>
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <div className="d-inline-block">
                                        {this.props.display.unitSerialNumber && (
                                            <div className="f-12">
                                                    Unit S/N: {this.props.display.unitSerialNumber}
                                            </div>
                                        )}

                                        {this.unitDisplays && this.unitDisplays.length > 0 && (
                                            <div className="f-12">
                                                    Other: {this.unitDisplays.join(', ')}{' '}
                                                <div className="d-inline-block p-0">
                                                    {display.roleInUnit == 2 ? '(Secondary)' : '(Primary)'}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {this.startTime < this.chartConfig.firstAvailableSampleTime && (
                                        <div className="d-inline-block ms-auto">
                                            <small>
                                                {'Earliest Sample Time: ' +
                                                        this.chartConfig.firstAvailableSampleTime}
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        }
                    />
                )}

                {!loading && this.error && (
                    <div className="bg-gray-darker rounded h-100 p-2 d-flex flex-column p-relative">
                        {this.props.chartNumber &&
                            <span className="chart-number px-2 py-1">{this.props.chartNumber}</span>
                        }
                        <a onClick={this.openPopout} className="btn btn-link p-0 me-auto">
                            {display.serialNumber}{' '}
                            {display.roleInUnit != 0 &&
                                <div className="d-inline-block p-0">
                                    ({Enums.toString(Enums.RoleInUnit, display.roleInUnit)})
                                </div>
                            }
                        </a>
                        {this.props.display.unitSerialNumber && (
                            <div className="f-12">Unit S/N: {this.props.display.unitSerialNumber}</div>
                        )}
                        {this.unitDisplays && this.unitDisplays.length > 0 && (
                            <div className="f-12">
                                Other: {this.unitDisplays.join(', ')}{' '}
                                <div className="d-inline-block p-0">
                                    {display.roleInUnit == 2 ? '(Secondary)' : '(Primary)'}
                                </div>
                            </div>
                        )}
                        <div className="flex-items-center justify-content-center d-flex flex-grow-1">
                            <div className="d-block text-center">
                                <BsExclamationTriangle className="text-danger f-24 mb-2" />
                                <div className="mb-2">Failed to get Chart</div>
                                <button className="btn btn-primary" onClick={this.retryLoadChart}>
                                    Retry
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {!loading && !this.error && !this.selectedChart && (
                    <div className="bg-gray-darker rounded h-100 p-2 d-flex flex-column p-relative">
                        {this.props.chartNumber &&
                            <span className="chart-number px-2 py-1">{this.props.chartNumber}</span>
                        }

                        <a onClick={this.openPopout} className="btn btn-link p-0 me-auto">
                            {display.serialNumber}{' '}
                            {display.roleInUnit != 0 &&
                                <div className="d-inline-block p-0">
                                    ({Enums.toString(Enums.RoleInUnit, display.roleInUnit)})
                                </div>
                            }
                        </a>
                        {this.props.display.unitSerialNumber && (
                            <div className="f-12">
                                <a>Unit S/N: {this.props.display.unitSerialNumber}</a>
                            </div>
                        )}
                        {this.unitDisplays && this.unitDisplays.length > 0 && (
                            <div className="f-12">
                                Other: {this.unitDisplays.join(', ')}{' '}
                                <div className="d-inline-block p-0">
                                    {display.roleInUnit == 2 ? '(Secondary)' : '(Primary)'}
                                </div>
                            </div>
                        )}
                        <div className="flex-items-center justify-content-center d-flex flex-grow-1">
                            <div className="d-block text-center">
                                <BsExclamationCircleFill className=" text-info f-24" /> &nbsp; The selected chart is not
                                available for this display.
                            </div>
                        </div>
                    </div>
                )}

                {loading && (
                    <div className="bg-gray-darker rounded h-100 p-2 d-flex flex-column p-relative">
                        {this.props.chartNumber &&
                            <span className="chart-number px-2 py-1">{this.props.chartNumber}</span>
                        }

                        <a onClick={this.openPopout} className="btn btn-link p-0 me-auto">
                            {display.serialNumber}{' '}
                            {display.roleInUnit != 0 &&
                                <div className="d-inline-block p-0">
                                    ({Enums.toString(Enums.RoleInUnit, display.roleInUnit)})
                                </div>
                            }
                        </a>
                        {this.props.display.unitSerialNumber && (
                            <div className="f-12">
                                <a>Unit S/N: {this.props.display.unitSerialNumber}</a>
                            </div>
                        )}
                        {this.unitDisplays && this.unitDisplays.length > 0 && (
                            <div className="f-12">
                                Other: {this.unitDisplays.join(', ')}{' '}
                                <div className="d-inline-block p-0">
                                    {display.roleInUnit == 2 ? '(Secondary)' : '(Primary)'}
                                </div>
                            </div>
                        )}
                        <div className="flex-items-center justify-content-center d-flex flex-grow-1">
                            <Loader loading={this.loading} message="Loading Data" showSpinner center />
                        </div>
                    </div>
                )}

                {this.renderModals && (
                    <DisplayDetailsPopout
                        statusPoll={this.statusPoll}
                        display={display}
                        popoutHandle={this.popoutHandle}
                    />
                )}
            </div>
        );
    }

    @bind
    @action
    private retryLoadChart() {
        this.error = null;
        this.errorCount = 0;
        this.loadDataAsync(1);
    };
    
    @bind
    @action
    private async loadDataAsync(maxAttempts: number) {
        this.loading = true;
        let attempts = 0;

        while (attempts < maxAttempts) {
            try {
                await this.displaysChartsStore.loadChartConfigAsync(this.props.display.serialNumber);
                if (this.props.display.unitSerialNumber) {
                    await this.unitsStore.loadAsync(this.props.display.unitSerialNumber);
                }

                if (this.chartConfig) {
                    if (!this.appStore.storage.liveChartSettings.liveChartSelectedChartName && this.chartConfig) {
                        this.appStore.storage.liveChartSettings.liveChartSelectedChartName =
                            this.chartConfig.chartConfigs[0].chartName;
                    }

                    const initialStartTime = this.props.startTime ?? this.maxValue;
                    this.startTime = this.hasMoreDataThanWindow
                        ? initialStartTime
                        : this.chartConfig.firstAvailableSampleTime;
                    this.startTime = this.maxValue;
                    if (this.props.startTime) this.startTime = this.props.startTime;

                    await this.debouncedLoadTrackData();
                    this.error = null;
                    break;
                }
            } catch (e: any) {
                this.error = e;
                this.errorCount++;
                attempts++;
            }
        }

        this.loading = false;

        if (this.chartConfig) {
            const refreshRate = this.appStore.storage.liveChartSettings.secsPerSample > 60
                ? this.appStore.storage.liveChartSettings.secsPerSample * 2000
                : 120000;
            this.refreshTimer = window.setInterval(this.refresh, refreshRate);
        }
    }

    @bind
    @action
    private openPopout() {
        this.renderModals = true;
        this.popoutHandle.open();
    }

    @bind
    @action
    private toggleLiveChartScaling() {
        this.commonAppStore.liveChartStaticScaling = !this.commonAppStore.liveChartStaticScaling;
    }

    @bind
    @action
    private onStartTimeChange() {
        this.startTime = this.props.startTime;
    }

    @bind
    @action
    private onDataOutOfBoundsChange() {
        if (this.dataOutOfBounds == true) {
            this.commonAppStore.chartHasDataOutOfBounds = true;
        }
    }

    @bind
    @action
    private async loadTrackData() {
        if (!this.selectedChart || !this.startTime || !this.endTime) return;
       // if (this.chartConfig && this.endTime < this.chartConfig?.firstAvailableSampleTime) return;
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = undefined;
        }
        
        await this.loadLock.waitAsync();
        this.abortController = new AbortController();
        try {
            for (const trackInfo of this.selectedChart.trackInfo) {
                await this.displaysChartsStore.loadTrackDataAsync(
                    this.props.display.serialNumber,
                    trackInfo.varNum,
                    this.startTime!,
                    this.endTime!,
                    this.appStore.storage.liveChartSettings.secsPerSample,
                    this.liveRefreshing,
                    { signal: this.abortController.signal }
                );
            }

            this.liveRefreshing = false;
        } catch (e: any) {
            if (e instanceof DOMException) {
                console.info('Cancelled');
            } else {
                this.tasksStore.toastErrorTask('Load Track Data', e);
                this.error = e;
                this.loading = true;
                this.loading = false;
                this.liveRefreshing = false;
            }
        } finally {
            this.abortController = undefined;
            this.loadLock.release();
            this.loading = false;
        }
    }

    @bind
    @action
    private async refresh() {
        if (!this.liveRefresh || !this.chartConfig || this.loading || this.liveRefreshing) return;

        console.info('Refresh Charts');
        this.liveRefreshing = true;
        this.now = new Date();
        this.startTime = this.maxValue;
    }

    @computed
    private get hasMoreDataThanWindow() {
        if (!this.chartConfig) return false;

        return this.chartConfig.firstAvailableSampleTime < this.maxValue;
    }

    @computed
    private get endTime() {
        if (!this.startTime) return;

        return addDurationToDate(this.startTime, this.timeWindow);
    }

    @computed
    private get chartConfig() {
        return this.displaysChartsStore.displaysCharts.get(this.props.display.serialNumber);
    }

    @computed
    private get trackDatas() {
        return this.displaysChartsStore.displayTrackDatas.get(this.props.display.serialNumber);
    }

    @computed
    private get selectedChart() {
        if (this.chartConfig?.chartConfigs) {
            if (this.appStore.storage.liveChartSettings.liveChartSelectedChartName) {
                return this.chartConfig.chartConfigs.find(
                    c => c.chartName == this.appStore.storage.liveChartSettings.liveChartSelectedChartName
                );
            } else if (!!this.chartConfig.chartConfigs.length) {
                return this.chartConfig.chartConfigs[0];
            }
        }
    }

    @computed
    private get statusClassName() {
        if (!this.props.display) return;

        switch (this.props.display.state) {
            case 'good':
                return 'text-default';
            case 'exception':
                return 'text-info';
            case 'warning':
                return 'text-warning';
            case 'critical':
                return 'text-danger';
        }
    }

    @computed
    private get timeWindow() {
        return new Duration((this.appStore.storage.liveChartSettings.liveChartTimeWindow ?? 2) * 60 * 60 * 1000);
    }

    @computed
    private get maxValue() {
        return subDurationFromDate(this.now, this.timeWindow);
    }

    @computed
    private get unitDisplays() {
        const unit = this.unitsStore.units.get(this.props.display.unitSerialNumber);
        return unit?.displays.filter(d => d !== this.props.display.serialNumber);
    }

    @computed
    private get liveRefresh() {
        if (!this.startTime) return false;
        if (this.error) return false;
        return differenceInSeconds(this.maxValue, this.startTime) < 60;
    }

    @computed
    private get startTimeMismatch() {
        return !this.liveRefresh && this.props.startTime != this.startTime;
    }

    @computed
    private get displayLocation() {
        const { display } = this.props;
        const location: string[] = [];

        if (display.unitLocation) {
            if (display.unitLocation.city) location.push(display.unitLocation.city);
            if (display.unitLocation.stateOrProvince) location.push(display.unitLocation.stateOrProvince);
        }

        return location.join(', ');
    }

    @computed
    private get tooltipMessage() {
        const { display } = this.props;

        const message: string[] = [];

        if (display.displayConnectionData?.customerDisplayID)
            message.push(display.displayConnectionData?.customerDisplayID);
        if (display.unitLocation?.customerSiteID) message.push(display.unitLocation?.customerSiteID);

        return message.join(', ');
    }

    @computed
    private get activeDisplayStatus() {
        if (this.props.display) return this.displaysStore.statuses.get(this.props.display.serialNumber);
    }

    @computed
    private get statusPoll() {
        if (this.activeDisplayStatus) return this.activeDisplayStatus.lastKnownPoll.displayStatusPoll;
    }

    @computed
    private get dataOutOfBounds() {
        if (this.selectedChart && this.trackDatas)
            return this.selectedChart.trackInfo.some(t => {
                const newMin = t.uniqueYScale ? t.yScaleMin : this.selectedChart!.yScaleMin;
                const newMax = t.uniqueYScale ? t.yScaleMax : this.selectedChart!.yScaleMax;
                const trackData = this.trackDatas!.get(t.varNum)?.trackData;
                if (trackData == null) return false;

                const [min, max] = minMax(trackData);

                return min < newMin || max > newMax;
            });
    }

    private abortController?: AbortController;
    private readonly loadLock = new Mutex();
    private debouncedLoadTrackData = debounce(this.loadTrackData, 250);
    private popoutHandle = new PopoutHandle();
    @observable
    private errorCount = 0;
    @observable
    private renderModals = false;
    @observable
    private loading = true;
    @observable
    private liveRefreshing = false;
    @observable
    private error: any;
    @observable
    private startTime?: Date;
    @observable
    private now = new Date();
    private refreshTimer?: number;
    @inject
    private appStore!: AppStore;
    @inject
    private tasksStore!: TasksStore;
    @inject
    private unitsStore!: UnitsStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private displaysChartsStore!: DisplaysChartsStore;
    @inject
    private commonAppStore!: CommonAppStore;
}
